import { useEffect, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { OrdersTable } from './OrdersTable';
import { OrderDetailsModal } from './OrderDetailsModal';
import { CreateOrderModal } from './CreateOrderModal';
import { Plus, Zap, ChevronDown, Utensils, Calendar } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { tablesService, RestaurantTable } from '../../services/tables.service';
import { toast } from 'sonner';

interface OrdersPageProps {
  onNavigate?: (page: string) => void;
  onOrderClick?: (orderId: string) => void;
}

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'refunded', label: 'Refunded' },
];

export function OrdersPage({ onNavigate, onOrderClick }: OrdersPageProps) {
  usePageTitle('Orders');

  const {
    orders: ordersData,
    loading,
    error,
    stats,
    total,
    limit,
    page,
    totalPages,
    createOrder,
    updateQuery,
    refresh,
  } = useOrders({ page: 1, limit: 50 });

  // Lightweight table list fetch for filter dropdown; avoid useTables side effects.
  const [tables, setTables] = useState<RestaurantTable[]>([]);

  useEffect(() => {
    const loadTables = async () => {
      try {
        const response = await tablesService.getTables();
        const payload: any = response.data;
        const rows: RestaurantTable[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        setTables(rows.filter((table) => table.isActive));
      } catch (error) {
        console.error('Failed to load tables for orders filter:', error);
        setTables([]);
      }
    };

    loadTables();
  }, []);

  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState('all');
  const [dateRangeDropdownOpen, setDateRangeDropdownOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [createOrderModalOpen, setCreateOrderModalOpen] = useState(false);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  const [selectedOrderId, _setSelectedOrderId] = useState('');

  const handleStatusSelect = (value: string) => {
    setSelectedStatus(value);
    setStatusDropdownOpen(false);
    
    // Update query with status filter
    if (value === 'all') {
      updateQuery({ status: undefined });
    } else {
      // Map UI status to backend status
      const statusMap: Record<string, string> = {
        'pending': 'PENDING',
        'confirmed': 'CONFIRMED',
        'preparing': 'PREPARING',
        'ready': 'READY',
        'delivered': 'DELIVERED',
        'closed': 'CLOSED',
        'cancelled': 'CANCELLED',
        'rejected': 'REJECTED',
        'refunded': 'REFUNDED',
      };
      updateQuery({ status: statusMap[value] as any });
    }
  };

  const handleCreateOrder = async (orderData: any) => {
    try {
      await createOrder(orderData);
      setCreateOrderModalOpen(false);
      toast.success('Order created successfully!');
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to create order. Please try again.');
    }
  };

  const handleDateRangeSelect = (value: string) => {
    if (value !== 'custom') {
      setSelectedDateRange(value);
      setDateRangeDropdownOpen(false);
    } else {
      setSelectedDateRange(value);
      // Keep dropdown open for custom date selection
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setDateRangeDropdownOpen(false);
      console.log('Custom date range:', { start: customStartDate, end: customEndDate });
    }
  };

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const selectedStatusLabel = statusOptions.find(opt => opt.value === selectedStatus)?.label || 'All Status';
  const selectedDateRangeLabel = dateRangeOptions.find(opt => opt.value === selectedDateRange)?.label || 'All Time';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="orders"
        onNavigate={onNavigate}
        ordersCount={stats?.total || ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Orders</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1">Manage all your orders</p>
              </div>
              
              {/* Filter Buttons */}
              <div className="flex items-center gap-3">
                {/* Status Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Zap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">{selectedStatusLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </button>
                  
                  {statusDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setStatusDropdownOpen(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleStatusSelect(option.value)}
                            className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              selectedStatus === option.value ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Table Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setTableDropdownOpen(!tableDropdownOpen)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Utensils className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">{selectedTable === 'all' ? 'All Tables' : selectedTable}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </button>
                  
                  {tableDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setTableDropdownOpen(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                        <button
                          key="all"
                          onClick={() => setSelectedTable('all')}
                          className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            selectedTable === 'all' ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'
                          }`}
                        >
                          All Tables
                        </button>
                        {tables.map((table) => (
                          <button
                            key={table.id}
                            onClick={() => setSelectedTable(table.name)}
                            className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              selectedTable === table.name ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'
                            }`}
                          >
                            {table.name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Date Range Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setDateRangeDropdownOpen(!dateRangeDropdownOpen)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">{selectedDateRangeLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                  </button>
                  
                  {dateRangeDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setDateRangeDropdownOpen(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                        {dateRangeOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleDateRangeSelect(option.value)}
                            className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              selectedDateRange === option.value ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                        {selectedDateRange === 'custom' && (
                          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                            <div className="mb-3">
                              <label className="block text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                              <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div className="mb-3">
                              <label className="block text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                              <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <button
                              onClick={handleCustomDateApply}
                              disabled={!customStartDate || !customEndDate}
                              className="w-full px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                            >
                              Apply Date Range
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Create Order Button */}
                <button
                  onClick={() => setCreateOrderModalOpen(true)}
                  className="flex items-center gap-2 px-3.5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-xs font-medium">Create Order</span>
                </button>
              </div>
            </div>

            {/* Orders Table */}
            {loading ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
                <div className="space-y-4">
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                <button 
                  onClick={refresh}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <OrdersTable 
                orders={ordersData}
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={(query) => updateQuery(query)}
                selectedStatus={selectedStatus} 
                selectedDateRange={selectedDateRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                selectedTable={selectedTable}
                onOrderClick={onOrderClick}
              />
            )}
          </div>
        </main>
      </div>

      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={createOrderModalOpen}
        onClose={() => setCreateOrderModalOpen(false)}
        onCreateOrder={handleCreateOrder}
      />

      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={orderDetailsModalOpen}
        onClose={() => setOrderDetailsModalOpen(false)}
        orderId={selectedOrderId}
      />
    </div>
  );
}